import axios from 'axios'
import toast from 'react-hot-toast'
import i18n from '@/i18n'
import { useAuthStore } from '@/stores/auth.store'
import { humanizeError } from './errorCopy'

const API_URL = import.meta.env.VITE_API_URL || ''

export const api = axios.create({
  baseURL: API_URL,
})

// Single in-flight refresh promise shared across all concurrent requests.
// Prevents the race condition where multiple parallel requests each try to
// refresh simultaneously, consuming the one-time refresh token more than once.
let refreshPromise: Promise<string> | null = null

function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) throw new Error('no_refresh_token')
    const { data } = await axios.post(`${API_URL}/v1/auth/token/refresh`, { refreshToken })
    localStorage.setItem('accessToken', data.accessToken)
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
    // Keep Zustand persisted state in sync so the next page load has the fresh token
    const store = useAuthStore.getState()
    if (store.user) store.setAuth(store.user, data.accessToken, data.refreshToken ?? undefined)
    return data.accessToken as string
  })().finally(() => { refreshPromise = null })
  return refreshPromise
}

// Returns true if the JWT expires within the next 5 minutes (or is already expired).
function isTokenExpiringSoon(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' && payload.exp - Date.now() / 1000 < 300
  } catch {
    return false
  }
}

// Proactively refresh before the request if the access token is about to expire.
// This eliminates the round-trip 401 → refresh → retry cycle that shows up in
// the browser console when the token expires between page loads.
api.interceptors.request.use(async (config) => {
  // When an admin is impersonating a user, use their session token instead
  const impersonationToken = sessionStorage.getItem('impersonationToken')
  if (impersonationToken) {
    config.headers.Authorization = `Bearer ${impersonationToken}`
    config.headers['Accept-Language'] = i18n.language || 'it'

    config.headers['X-eDive-Lang'] = i18n.language || 'it'
    return config
  }

  let token = localStorage.getItem('accessToken')
  if (token && isTokenExpiringSoon(token)) {
    try { token = await doRefresh() } catch { /* let the 401 handler take over */ }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`
  config.headers['Accept-Language'] = i18n.language || 'it'
  // Anche in un'intestazione nostra: `Accept-Language` non arriva ai servizi (il
  // 19/10/2026 l'AI rispondeva in inglese con l'interfaccia in italiano, e la prova
  // ha mostrato che la richiesta al servizio quell'intestazione non ce l'ha più).
  config.headers['X-eDive-Lang'] = i18n.language || 'it'
  return config
})

// ── 429 retry queue ──────────────────────────────────────────────────────────
// On rate-limit responses, retry transparently up to 3 times with exponential
// backoff (1.5 s → 3 s → 6 s), honouring the Retry-After header when present.
// Only surfaces an error toast after all retries are exhausted.
const RATE_LIMIT_MAX_RETRIES = 3
const RATE_LIMIT_BASE_MS     = 1_500

function rateLimitDelay(retryAfterHeader: string | undefined, attempt: number): number {
  const fromHeader = retryAfterHeader ? parseFloat(retryAfterHeader) * 1_000 : NaN
  return !isNaN(fromHeader) && fromHeader > 0
    ? fromHeader
    : RATE_LIMIT_BASE_MS * Math.pow(2, attempt - 1)
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const status = error.response?.status

    // ── 429 transparent retry ──────────────────────────────────────────────
    if (status === 429) {
      const attempt = (originalRequest._rateLimitRetry ?? 0) + 1
      if (attempt <= RATE_LIMIT_MAX_RETRIES) {
        originalRequest._rateLimitRetry = attempt
        const delay = rateLimitDelay(error.response.headers?.['retry-after'], attempt)
        await new Promise<void>(resolve => setTimeout(resolve, delay))
        return api(originalRequest)
      }
      // All retries exhausted — fall through to show the error
    }

    // ── 401 token refresh ──────────────────────────────────────────────────
    const isAuthEndpoint = originalRequest.url?.includes('/v1/auth/')
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true
      try {
        const newToken = await doRefresh()
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      } catch {
        useAuthStore.getState().logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    // ── Error toast ────────────────────────────────────────────────────────
    const raw = error.response?.data?.error ?? error.response?.data?.message ?? error.response?.data?.detail ?? error.message
    const backendMsg = typeof raw === 'string' ? raw
      : Array.isArray(raw) ? (raw[0]?.msg ?? raw[0]?.message ?? undefined)
      : undefined
    // Umanizza: tiene il messaggio del backend se utile, altrimenti tono di supporto.
    const msg = humanizeError(backendMsg, status)
    const isConsentRequired = error.response?.data?.error === 'consent_required'
    if (status !== 401 && !error.config?._skipToast && !isConsentRequired) {
      toast.error(msg)
    }
    return Promise.reject(error)
  }
)

export { api as client }
export default api
